// src/index.tsx
import { useState, useEffect, useRef } from "react";
import { GlobalWorkerOptions, getDocument, version } from "pdfjs-dist";
function isFunction(value) {
  return typeof value === "function";
}
var usePdf = ({
  canvasRef,
  file,
  onDocumentLoadSuccess,
  onDocumentLoadFail,
  onPageLoadSuccess,
  onPageLoadFail,
  onPageRenderSuccess,
  onPageRenderFail,
  scale = 1,
  rotate = 0,
  page = 1,
  cMapUrl,
  cMapPacked,
  workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.mjs`,
  withCredentials = false
}) => {
  const [pdfDocument, setPdfDocument] = useState();
  const [pdfPage, setPdfPage] = useState();
  const renderTask = useRef(null);
  const lastPageRequestedRenderRef = useRef(null);
  const onDocumentLoadSuccessRef = useRef(onDocumentLoadSuccess);
  const onDocumentLoadFailRef = useRef(onDocumentLoadFail);
  const onPageLoadSuccessRef = useRef(onPageLoadSuccess);
  const onPageLoadFailRef = useRef(onPageLoadFail);
  const onPageRenderSuccessRef = useRef(onPageRenderSuccess);
  const onPageRenderFailRef = useRef(onPageRenderFail);
  useEffect(() => {
    onDocumentLoadSuccessRef.current = onDocumentLoadSuccess;
  }, [onDocumentLoadSuccess]);
  useEffect(() => {
    onDocumentLoadFailRef.current = onDocumentLoadFail;
  }, [onDocumentLoadFail]);
  useEffect(() => {
    onPageLoadSuccessRef.current = onPageLoadSuccess;
  }, [onPageLoadSuccess]);
  useEffect(() => {
    onPageLoadFailRef.current = onPageLoadFail;
  }, [onPageLoadFail]);
  useEffect(() => {
    onPageRenderSuccessRef.current = onPageRenderSuccess;
  }, [onPageRenderSuccess]);
  useEffect(() => {
    onPageRenderFailRef.current = onPageRenderFail;
  }, [onPageRenderFail]);
  useEffect(() => {
    GlobalWorkerOptions.workerSrc = workerSrc;
  }, [workerSrc]);
  useEffect(() => {
    const config = { url: file, withCredentials };
    if (cMapUrl) {
      config.cMapUrl = cMapUrl;
      config.cMapPacked = cMapPacked;
    }
    getDocument(config).promise.then(
      (loadedPdfDocument) => {
        setPdfDocument(loadedPdfDocument);
        if (isFunction(onDocumentLoadSuccessRef.current)) {
          onDocumentLoadSuccessRef.current(loadedPdfDocument);
        }
      },
      () => {
        if (isFunction(onDocumentLoadFailRef.current)) {
          onDocumentLoadFailRef.current();
        }
      }
    );
  }, [file, withCredentials, cMapUrl, cMapPacked]);
  useEffect(() => {
    const drawPDF = (page2) => {
      const rotation = rotate === 0 ? page2.rotate : page2.rotate + rotate;
      const viewport = page2.getViewport({ scale, rotation });
      const canvasEl = canvasRef.current;
      if (!canvasEl) {
        return;
      }
      const canvasContext = canvasEl.getContext("2d");
      if (!canvasContext) {
        return;
      }
      canvasEl.height = viewport.height * window.devicePixelRatio;
      canvasEl.width = viewport.width * window.devicePixelRatio;
      canvasContext.scale(window.devicePixelRatio, window.devicePixelRatio);
      if (renderTask.current) {
        lastPageRequestedRenderRef.current = page2;
        renderTask.current.cancel();
        return;
      }
      renderTask.current = page2.render({
        canvasContext,
        viewport
      });
      return renderTask.current.promise.then(
        () => {
          renderTask.current = null;
          if (isFunction(onPageRenderSuccessRef.current)) {
            onPageRenderSuccessRef.current(page2);
          }
        },
        (reason) => {
          renderTask.current = null;
          if (reason && reason.name === "RenderingCancelledException") {
            const lastPageRequestedRender = lastPageRequestedRenderRef.current ?? page2;
            lastPageRequestedRenderRef.current = null;
            drawPDF(lastPageRequestedRender);
          } else if (isFunction(onPageRenderFailRef.current)) {
            onPageRenderFailRef.current();
          }
        }
      );
    };
    if (pdfDocument) {
      pdfDocument.getPage(page).then(
        (loadedPdfPage) => {
          setPdfPage(loadedPdfPage);
          if (isFunction(onPageLoadSuccessRef.current)) {
            onPageLoadSuccessRef.current(loadedPdfPage);
          }
          drawPDF(loadedPdfPage);
        },
        () => {
          if (isFunction(onPageLoadFailRef.current)) {
            onPageLoadFailRef.current();
          }
        }
      );
    }
  }, [canvasRef, page, pdfDocument, rotate, scale]);
  return { pdfDocument, pdfPage };
};
export {
  usePdf
};
